/**
 * HTTP core (U6): fetch + zod parse + error envelope + token refresh.
 *
 * Every panel request goes through `request()`. Responses are ALWAYS parsed
 * with a shared zod schema — the typed client never trusts raw JSON. Errors
 * arrive as the standard NestJS envelope and are rethrown as ApiRequestError.
 *
 * Session handling: bearer token from the zustand session store; a 401 (or a
 * locally-expired access token) triggers a single-flight refresh + one retry.
 * A failed refresh clears the session, which route guards react to.
 */
import { z } from 'zod';
import { ApiErrorSchema, RefreshResponseSchema } from '@buildledger/shared';
import { useSession } from './session';

export const API_BASE = '/api/v1';

// ------------------------------------------------------------------- errors

/** A non-2xx API response, carrying the parsed error envelope. */
export class ApiRequestError extends Error {
  readonly statusCode: number;
  /** Envelope `error` field, e.g. "Unauthorized", "Conflict". */
  readonly code: string;
  /** Envelope messages (validation errors arrive as arrays). */
  readonly messages: readonly string[];
  readonly details: unknown;

  constructor(statusCode: number, code: string, messages: readonly string[], details?: unknown) {
    super(messages[0] ?? code);
    this.name = 'ApiRequestError';
    this.statusCode = statusCode;
    this.code = code;
    this.messages = messages;
    this.details = details;
  }
}

/** The server answered 2xx but the body violated the shared contract. */
export class ApiContractError extends Error {
  readonly issues: z.ZodIssue[];

  constructor(path: string, error: z.ZodError) {
    super(`Response from ${path} violated the shared contract: ${error.issues[0]?.message ?? ''}`);
    this.name = 'ApiContractError';
    this.issues = error.issues;
  }
}

/** Network failure (API unreachable) — distinct from an HTTP error. */
export class ApiNetworkError extends Error {
  constructor(path: string, cause: unknown) {
    super(`Could not reach the API at ${path}`);
    this.name = 'ApiNetworkError';
    this.cause = cause;
  }
}

/** Human-friendly one-liner for any error thrown by the API layer. */
export function describeApiError(error: unknown): string {
  if (error instanceof ApiRequestError) return error.messages.join(' · ') || error.code;
  if (error instanceof ApiNetworkError) return 'The API is unreachable. Is the server running?';
  if (error instanceof ApiContractError) return 'Unexpected response shape from the API.';
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}

// -------------------------------------------------------------------- query

export type QueryValue = string | number | boolean | null | undefined;

export function buildQuery(params: Record<string, QueryValue> | undefined): string {
  if (!params) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

// ------------------------------------------------------------------ refresh

let refreshInFlight: Promise<boolean> | null = null;

async function performRefresh(): Promise<boolean> {
  const { tokens } = useSession.getState();
  if (!tokens) return false;
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refreshToken: tokens.refreshToken }),
    });
    if (!res.ok) {
      useSession.getState().clear();
      return false;
    }
    const body = RefreshResponseSchema.parse(await res.json());
    useSession.getState().setTokens(body.tokens);
    return true;
  } catch {
    // Network failure: keep the session; the original request will surface it.
    return false;
  }
}

/** Single-flight token refresh — concurrent 401s share one refresh call. */
export function refreshSession(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = performRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

function accessTokenLooksExpired(): boolean {
  const { tokens } = useSession.getState();
  if (!tokens) return false;
  const expires = Date.parse(tokens.accessExpiresAt);
  return Number.isFinite(expires) && expires - Date.now() < 10_000;
}

// ------------------------------------------------------------------ request

export interface RequestOptions<TSchema extends z.ZodTypeAny> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Path under /api/v1, e.g. `/estimates/${id}`. */
  path: string;
  query?: Record<string, QueryValue>;
  /** JSON body (plain object) or FormData for multipart uploads. */
  body?: unknown;
  /** Shared zod schema the response body must satisfy. */
  schema: TSchema;
  /** Attach the bearer token + refresh-on-401 (default true). */
  auth?: boolean;
  signal?: AbortSignal;
}

async function rawFetch(
  options: RequestOptions<z.ZodTypeAny>,
  withAuth: boolean,
): Promise<Response> {
  const headers: Record<string, string> = {};
  let body: BodyInit | undefined;
  if (options.body instanceof FormData) {
    body = options.body; // browser sets the multipart boundary header
  } else if (options.body !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(options.body);
  }
  if (withAuth) {
    const { tokens } = useSession.getState();
    if (tokens) headers['authorization'] = `Bearer ${tokens.accessToken}`;
  }
  const path = `${API_BASE}${options.path}${buildQuery(options.query)}`;
  try {
    return await fetch(path, {
      method: options.method ?? 'GET',
      headers,
      body,
      signal: options.signal ?? null,
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiNetworkError(path, cause);
  }
}

async function toApiError(res: Response): Promise<ApiRequestError> {
  let envelope: unknown;
  try {
    envelope = await res.json();
  } catch {
    envelope = undefined;
  }
  const parsed = ApiErrorSchema.safeParse(envelope);
  if (parsed.success) {
    const { statusCode, error, message, details } = parsed.data;
    return new ApiRequestError(
      statusCode,
      error,
      Array.isArray(message) ? message : [message],
      details,
    );
  }
  return new ApiRequestError(res.status, res.statusText || 'Error', [
    `Request failed with status ${res.status}`,
  ]);
}

/** Where the Billing page sends the owner back once the subscription is active. */
export const BILLING_RETURN_KEY = 'bl.billing.return';

/**
 * 402 PAYMENT_REQUIRED: the company has not paid (Stripe billing, docs/BILLING.md).
 * Every screen behind it would fail anyway, so go straight to the Billing page,
 * remembering where the user was (e.g. the trades step of setup).
 */
function sendToBilling(): void {
  if (typeof window === 'undefined' || window.location.pathname === '/billing') return;
  try {
    window.sessionStorage.setItem(BILLING_RETURN_KEY, window.location.pathname + window.location.search);
  } catch {
    // storage blocked — the page falls back to the dashboard
  }
  window.location.replace('/billing');
}

/**
 * Perform a request and parse the response with `schema`.
 * 204/empty responses parse `undefined` (use a z.void()/OkResponse schema).
 */
export async function request<TSchema extends z.ZodTypeAny>(
  options: RequestOptions<TSchema>,
): Promise<z.infer<TSchema>> {
  const withAuth = options.auth ?? true;

  // Proactive refresh: don't fire a request we know will 401.
  if (withAuth && accessTokenLooksExpired()) await refreshSession();

  let res = await rawFetch(options, withAuth);

  if (res.status === 401 && withAuth && useSession.getState().tokens) {
    const refreshed = await refreshSession();
    if (refreshed) {
      res = await rawFetch(options, withAuth);
    } else {
      useSession.getState().clear();
    }
  }

  if (res.status === 402 && withAuth) sendToBilling();
  if (!res.ok) throw await toApiError(res);

  let payload: unknown;
  if (res.status === 204) {
    payload = undefined;
  } else {
    const text = await res.text();
    payload = text.length === 0 ? undefined : (JSON.parse(text) as unknown);
  }

  const parsed = options.schema.safeParse(payload);
  if (!parsed.success) throw new ApiContractError(options.path, parsed.error);
  return parsed.data as z.infer<TSchema>;
}

/** Authenticated binary fetch for files/PDFs that cannot use a bare <img>/<a> URL. */
export async function requestBlob(path: string): Promise<Blob> {
  if (accessTokenLooksExpired()) await refreshSession();
  const options = { path, schema: z.unknown() };
  let res = await rawFetch(options, true);
  if (res.status === 401 && useSession.getState().tokens) {
    const refreshed = await refreshSession();
    if (refreshed) res = await rawFetch(options, true);
    else useSession.getState().clear();
  }
  if (!res.ok) throw await toApiError(res);
  return res.blob();
}
